import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { EmployerCreditModel } from '../../../core/models/employer-credit.model';
import { RolloverDay } from '../../../core/models/rollover-day';
import { RolloverPercentage } from '../../../core/models/rollover-percentage';
import { CreditManagementService } from '../../../core/services/api-services/credit-management.service';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { BreadcrumbComponent, BreadcrumbItem } from "../../../shared/components/breadcrumb/breadcrumb.component";
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { PageEvent, PaginatorComponent } from "../../../shared/components/paginator/paginator.component";
import { FilterPanelComponent } from "../../../shared/components/filter-panel/filter-panel.component";
import { FilterField, FilterResult } from '../../../shared/components/filter-panel/model/filter-field.model';
import { FormattedNumberInputComponent } from '../../../shared/components/formatted-number-input/formatted-number-input.component';


@Component({
  selector: 'app-credit-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, BreadcrumbComponent, PaginatorComponent, FilterPanelComponent, FormattedNumberInputComponent],
  templateUrl: './credit-management.component.html',
  styleUrl: './credit-management.component.css'
})
export class CreditManagementComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();


  @ViewChild('creditLineRequestOffcanvas') offcanvasElementRef!: ElementRef;
  activeTab: 'employerCredits' | 'creditRiskRequests' | 'pendingApprovals' = 'employerCredits';

  searchText = '';
  // companyIdQuery: string = '9430692498';

  employerCredits: EmployerCreditModel[] = [];
  filteredEmployerCredits: EmployerCreditModel[] = [];
  selectedCompany: any | null = null;
  selectedPending: any | null = null;
  offcanvasContext: 'request' | 'pending' = 'request';

  creditLineForm!: FormGroup;

  // Credit Line Requests tab state
  clrSearchText = '';
  creditLineRequests: any[] = [];
  filteredCreditLineRequests: any[] = [];

  pendingSearchText = '';
  pendingApprovals: any[] = [];
  filteredPendingApprovals: any[] = [];

  ecIsLoading = false;
  clrIsLoading = false;
  paIsLoading = false;
  ecError = '';
  clrError = '';
  paError = '';

  rollOverPercentages: Array<RolloverPercentage> = [];
  rollOverDays: Array<RolloverDay> = [];
  selectedPercentageIds: number[] = [];
  selectedDayIds: number[] = [];

  dataSubscriber$!: Subscription;

  loggedPortalAdminId!: number;


  //==========================================================//
  // Variables to store original values for change detection //
  //========================================================//
  private originalCreditLimit?: number | null;
  private originalWageWithdrawalPercentage?: number | null;
  private originalPercentageId?: number;
  private originalDayId?: number;
  //====================================================//

  // Pagination variables //
  employerCreditPage: number = 1;
  employerCreditPageSize: number = 10;
  pagedEmployerCredits: any[] = [];

  creditLineRequestsPage: number = 1;
  creditLineRequestsPageSize: number = 10
  pagedCreditLineRequests: any[] = [];

  pendingApprovalsPage: number = 1
  pendingApprovalsPageSize: number = 10;
  pagedPendingApprovals: any[] = [];

  // Employer Credits
  ecCurrentPage: number = 1; ecPageSize: number = 10; ecTotal: number = 0;

  // Credit Line Requests
  clrCurrentPage: number = 1; clrPageSize: number = 10; clrTotal: number = 0;

  // Pending Approvals
  paCurrentPage: number = 1; paPageSize: number = 10; paTotal: number = 0;

  ecFilterValue: string = '';
  clrFilterValue: string = '';
  paFilterValue: string = '';

  // =============== Server filter Props =============== //

  filterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'CompanyId',
      options: [
        {
          value: 'CompnayId',
          label: 'Compnay ID',
          field: {
            type: 'text',
            controlName: 'CompanyId',            // key sent to backend
            placeholder: 'Enter Company ID',
            hint: 'Example: 9412265199 (10 digits)',
            validators: [Validators.required, Validators.pattern(/^\d{10}$/), Validators.minLength(10), Validators.maxLength(10)],
          },
        }
      ],
    },
  ];

  // =================================================== //

  constructor(
    private creditService: CreditManagementService,
    private fb: FormBuilder,
    protected authService: AuthService,
    private toastr: ToastrService,
    private breadcrumbService: BreadcrumbService,
    private toaster: ToastrService
  ) {

    this.dataSubscriber$ = new Subscription();
    this.loggedPortalAdminId = this.authService.getUser()?.portalAdminId || 0;
  }

  ngOnInit(): void {
    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Credit & Risk', icon: 'bi bi-shield-exclamation' },
      { label: 'Manage Credit', icon: 'bi bi-credit-card' }
    ]);

    this.fetchRolloverPercenatges();
    this.fetchRolloverDays();
    this.fetchEmployerCredits(this.ecCurrentPage, this.ecPageSize, this.ecFilterValue || undefined);
    this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize, this.paFilterValue || undefined);
    this.initCreditLineForm();
  }

  setActiveTab(tab: 'employerCredits' | 'creditRiskRequests' | 'pendingApprovals'): void {

    // Only refresh if switching to a different tab
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;

    // Reset search text when switching tabs
    this.searchText = '';
    this.clrSearchText = '';
    this.pendingSearchText = '';

    // Auto-refresh data when switching tabs
    if (tab === 'employerCredits') {
      this.fetchEmployerCredits(this.ecCurrentPage, this.ecPageSize, this.ecFilterValue || undefined);
    } else if (tab === 'creditRiskRequests') {
      this.fetchCreditLineRequests(this.clrCurrentPage, this.clrPageSize, this.clrFilterValue || undefined);
    } else if (tab === 'pendingApprovals') {
      this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize, this.paFilterValue || undefined);
    }
  }

  // Get Roolover Percentages
  fetchRolloverPercenatges(): void {
    this.dataSubscriber$.add(
      this.creditService.getRolloverPercentages().subscribe({
        next: (response) => {
          if (response?.success) {
            this.rollOverPercentages = response.data || [];
          } else {
            console.error('Failed to fetch rollover percentages:', response?.message);
            this.rollOverPercentages = [];
          }
        },
        error: (error) => {
          console.error('Error fetching rollover percentages:', error);
          this.rollOverPercentages = [];
        }
      })
    );
  }

  // Get Roolover Days
  fetchRolloverDays(): void {
    this.dataSubscriber$.add(
      this.creditService.getRolloverDays().subscribe({
        next: (response) => {
          if (response?.success) {
            this.rollOverDays = response.data || [];
          } else {
            console.error('Failed to fetch rollover days:', response?.message);
            this.rollOverDays = [];
          }
        },
        error: (error) => {
          console.error('Error fetching rollover days:', error);
          this.rollOverDays = [];
        }
      })
    );
  }

  fetchEmployerCredits(page: number, pageSize: number, companyId?: string): void {
    this.ecIsLoading = true;
    this.dataSubscriber$.add(
      this.creditService.getEmployerCreditsPaginated(page, pageSize, companyId).subscribe({
        next: (res) => {
          if (res.success) {
            this.employerCredits = (res.data ?? []).map((item: any) => ({ ...item }));
            this.filteredEmployerCredits = [...this.employerCredits];
            this.ecTotal = res.totalCount;
            this.ecCurrentPage = res.pageNumber;
            this.ecPageSize = res.pageSize;
            this.ecError = '';
          } else {
            this.employerCredits = [];
            this.filteredEmployerCredits = [];
            this.ecError = res?.message || 'Failed to load employer credits.';
          }
          this.ecIsLoading = false;
        },
        error: (err) => {
          this.ecIsLoading = false;
          this.ecError = err.message || 'Error loading employer credits';
        }
      })
    );
  }

  fetchCreditLineRequests(page: number, pageSize: number, companyId?: string): void {
    this.clrIsLoading = true;
    this.dataSubscriber$.add(
      this.creditService.getCreditLineRequestsPaginated(page, pageSize, companyId).subscribe({
        next: (res) => {
          if (res.success) {
            this.creditLineRequests = res.data ?? [];
            this.filteredCreditLineRequests = [...this.creditLineRequests];
            this.clrTotal = res.totalCount;
            this.clrCurrentPage = res.pageNumber;
            this.clrPageSize = res.pageSize;
            this.clrError = '';
          } else {
            this.creditLineRequests = [];
            this.filteredCreditLineRequests = [];
            this.clrError = res.message || 'Failed to load credit line requests.';
          }
          this.clrIsLoading = false;
        },
        error: (err) => {
          this.creditLineRequests = [];
          this.filteredCreditLineRequests = [];
          this.clrError = err?.message || 'Error loading credit line requests';
          this.clrIsLoading = false;
        }
      })
    );
  }

  performClrSearch(): void {
    const searchText = this.clrSearchText.trim().toLowerCase();
    if (!searchText) {
      this.filteredCreditLineRequests = [...this.creditLineRequests];
      this.resetCreditLineRequestsPagination();
      return;
    }
    this.filteredCreditLineRequests = this.creditLineRequests.filter(creditLineRequest =>
      Object.values(creditLineRequest).some(val =>
        String(val).toLowerCase().includes(searchText)
      )
    );
    this.resetCreditLineRequestsPagination();
  }

  fetchPendingApprovals(page: number, pageSize: number, companyId?: string): void {
    this.paIsLoading = true;
    this.dataSubscriber$.add(
      this.creditService.getPendingApprovalsPaginated(page, pageSize, companyId).subscribe({
        next: (res) => {
          if (res.success) {
            this.pendingApprovals = res.data ?? [];
            this.filteredPendingApprovals = [...this.pendingApprovals];
            this.paTotal = res.totalCount;
            this.paCurrentPage = res.pageNumber;
            this.paPageSize = res.pageSize;
            this.paError = '';
          } else {
            this.pendingApprovals = [];
            this.filteredPendingApprovals = [];
            this.paError = res.message || 'Failed to load pending approvals.';
          }
          this.paIsLoading = false;
        },
        error: (err) => {
          this.pendingApprovals = [];
          this.filteredPendingApprovals = [];
          this.paError = err?.message || 'Error loading pending approvals';
          this.paIsLoading = false;
        }
      })
    );
  }


  performPendingSearch(): void {
    const searchText = this.pendingSearchText.trim().toLowerCase();
    if (!searchText) {
      this.filteredPendingApprovals = [...this.pendingApprovals];
      this.resetPendingApprovalsPagination();
      return;
    }
    this.filteredPendingApprovals = this.pendingApprovals.filter(pendingCreditLineRequest =>
      Object.values(pendingCreditLineRequest).some(val =>
        String(val).toLowerCase().includes(searchText)
      )
    );
    this.resetPendingApprovalsPagination();
  }

  performSearch(): void {
    const searchText = this.searchText.trim().toLowerCase();
    if (!searchText) {
      this.filteredEmployerCredits = [...this.employerCredits];
      this.resetEmployerCreditPagination();
      return;
    }

    this.filteredEmployerCredits = this.employerCredits.filter(employergCreditLine =>
      Object.values(employergCreditLine).some(val =>
        String(val).toLowerCase().includes(searchText)
      )
    );
    this.resetEmployerCreditPagination();
  }

  initCreditLineForm(): void {
    this.creditLineForm = this.fb.group({
      creditLimit: [null, [Validators.required, Validators.min(0)]],
      wageWithdrawalPercentage: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
      rollOverPercentageId: [null, Validators.required],
      rollOverDayId: [null, Validators.required],
    });
  }

  getFormErrors(): any {
    const errors: any = {};
    Object.keys(this.creditLineForm.controls).forEach(key => {
      const control = this.creditLineForm.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  onWageWithdrawalInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = Number(input.value);
    if (isNaN(value)) {
      this.creditLineForm.get('wageWithdrawalPercentage')?.setValue('');
      return;
    }
    if (value > 100) value = 100;
    if (value < 0) value = 0;
    // Normalize to at most two decimals
    value = Math.round(value * 100) / 100;
    this.creditLineForm.get('wageWithdrawalPercentage')?.setValue(value, { emitEvent: false });
  }

  openCreditLineRequest(company: any): void {
    this.selectedCompany = company;
    this.offcanvasContext = 'request';

    // Reset form completely first
    this.creditLineForm.reset();
    this.selectedPercentageIds = [];
    this.selectedDayIds = [];

    //console.log('Company data:', company);

    const storedPercentageValue = company.rollOverPercentage;
    const storedDayValue = company.rollOverDay;

    // Select all percentages whose value is <= the stored value
    if (storedPercentageValue != null) {
      this.selectedPercentageIds = this.rollOverPercentages
        .filter(r => r.rollOverPercentage <= storedPercentageValue)
        .map(r => r.rollOverPercentageId);
    }

    // Select all days whose value is <= the stored value
    if (storedDayValue != null) {
      this.selectedDayIds = this.rollOverDays
        .filter(r => r.rollOverDay <= storedDayValue)
        .map(r => r.rollOverDayId);
    }

    // Get the ID of the option whose value matches the stored value
    const matchedPercentageId = this.rollOverPercentages.find(
      r => r.rollOverPercentage === storedPercentageValue
    )?.rollOverPercentageId ?? null;

    const matchedDayId = this.rollOverDays.find(
      r => r.rollOverDay === storedDayValue
    )?.rollOverDayId ?? null;

    // Pre-fill ALL values with SINGULAR field names
    this.creditLineForm.patchValue({
      creditLimit: this.activeTab === 'creditRiskRequests' ? null : company.creditLimit ?? null,
      wageWithdrawalPercentage: this.activeTab === 'creditRiskRequests' ? null : company.wageWithdrawalPercentage ?? null,
      rollOverPercentageId: matchedPercentageId,
      rollOverDayId: matchedDayId
    });

    this.storeOriginalValues();
    // Mark as pristine and untouched after setting values
    this.creditLineForm.markAsUntouched();
    this.creditLineForm.markAsPristine();

    const offcanvasEl: any = this.offcanvasElementRef?.nativeElement;
    if (offcanvasEl && (window as any).bootstrap) {
      const bs = (window as any).bootstrap.Offcanvas.getInstance(offcanvasEl) || new (window as any).bootstrap.Offcanvas(offcanvasEl);
      bs.show();
    }
  }

  closeOffcanvas(): void {
    // Reset form and selections when closing
    this.creditLineForm.reset();
    this.selectedPercentageIds = [];
    this.selectedDayIds = [];
    this.selectedCompany = null;

    const offcanvasEl: any = this.offcanvasElementRef?.nativeElement;
    if (offcanvasEl && (window as any).bootstrap) {
      const bs = (window as any).bootstrap.Offcanvas.getInstance(offcanvasEl) || new (window as any).bootstrap.Offcanvas(offcanvasEl);
      bs.hide();
    }
  }

  //=====================================================================//
  // Helper Methods to check if a percentages or days option is selected//
  //===================================================================//
  // Check if a percentage checkbox is checked
  isPercentageChecked(id: number): boolean {
    return this.selectedPercentageIds.includes(id);
  }

  // Check if a day checkbox is checked
  isDayChecked(id: number): boolean {
    return this.selectedDayIds.includes(id);
  }

  // Handle percentage checkbox change
  onPercentageChange(option: any, event: any): void {
    const isChecked = event.target.checked;
    const clickedValue = option.rollOverPercentage;

    if (isChecked) {
      // Select all options whose value is <= the clicked value
      this.selectedPercentageIds = this.rollOverPercentages
        .filter(r => r.rollOverPercentage <= clickedValue)
        .map(r => r.rollOverPercentageId);
    } else {
      // Deselect this option and all options with value >= clicked value
      this.selectedPercentageIds = this.rollOverPercentages
        .filter(r => r.rollOverPercentage < clickedValue)
        .map(r => r.rollOverPercentageId);
    }

    // Get the ID of the option with the highest value among the selected ones
    const topPercentageOption = this.rollOverPercentages
      .filter(r => this.selectedPercentageIds.includes(r.rollOverPercentageId))
      .reduce((max, r) => r.rollOverPercentage > max.rollOverPercentage ? r : max, { rollOverPercentageId: 0, rollOverPercentage: -Infinity } as RolloverPercentage);

    // Patch with SINGULAR field name
    this.creditLineForm.patchValue({
      rollOverPercentageId: this.selectedPercentageIds.length > 0 ? topPercentageOption.rollOverPercentageId : null
    });
  }

  // Handle day checkbox change
  onDayChange(option: any, event: any): void {
    const isChecked = event.target.checked;
    const clickedValue = option.rollOverDay;

    if (isChecked) {
      // Select all options whose value is <= the clicked value
      this.selectedDayIds = this.rollOverDays
        .filter(r => r.rollOverDay <= clickedValue)
        .map(r => r.rollOverDayId);
    } else {
      // Deselect this option and all options with value >= clicked value
      this.selectedDayIds = this.rollOverDays
        .filter(r => r.rollOverDay < clickedValue)
        .map(r => r.rollOverDayId);
    }

    // Get the ID of the option with the highest value among the selected ones
    const topDayOption = this.rollOverDays
      .filter(r => this.selectedDayIds.includes(r.rollOverDayId))
      .reduce((max, r) => r.rollOverDay > max.rollOverDay ? r : max, { rollOverDayId: 0, rollOverDay: -Infinity } as RolloverDay);

    // Patch with SINGULAR field name
    this.creditLineForm.patchValue({
      rollOverDayId: this.selectedDayIds.length > 0 ? topDayOption.rollOverDayId : null
    });
  }

  //=====================================================================//
  // Method to check if any form field has changed from original values //
  //===================================================================//
  private checkForChanges(
    newCreditLimit: number,
    newWageWithdrawalPercentage: number,
    newPercentageId: number | null,
    newDayId: number | null
  ): boolean {
    // If there's no existing data to compare against, allow submission
    if (!this.selectedCompany) {
      return true;
    }

    // Compare with original values
    // You'll need to store original values when loading the form
    // Assuming you have these stored in component properties:
    const creditLimitChanged = this.originalCreditLimit !== undefined
      ? Number(newCreditLimit) !== Number(this.originalCreditLimit)
      : true;

    const wagePercentageChanged = this.originalWageWithdrawalPercentage !== undefined
      ? Number(newWageWithdrawalPercentage) !== Number(this.originalWageWithdrawalPercentage)
      : true;

    const percentageIdChanged = this.originalPercentageId !== undefined
      ? newPercentageId !== this.originalPercentageId
      : true;

    const dayIdChanged = this.originalDayId !== undefined
      ? newDayId !== this.originalDayId
      : true;

    // Return true if ANY field has changed
    return creditLimitChanged || wagePercentageChanged || percentageIdChanged || dayIdChanged;
  }

  //=============================================//
  // Store original values for change detection  //
  //=============================================//
  private storeOriginalValues(): void {
    if (this.activeTab === 'creditRiskRequests') {
      // Clear selected arrays
      this.originalCreditLimit = null;
      this.originalWageWithdrawalPercentage = null;
      this.selectedPercentageIds = [];
      this.selectedDayIds = [];

      return;
    }

    // For other tabs, store the current form values
    this.originalCreditLimit = this.creditLineForm.get('creditLimit')?.value;
    this.originalWageWithdrawalPercentage = this.creditLineForm.get('wageWithdrawalPercentage')?.value;

    // Store the ID of the highest-value selected percentage and day options
    this.originalPercentageId = this.selectedPercentageIds.length > 0
      ? this.rollOverPercentages
          .filter(r => this.selectedPercentageIds.includes(r.rollOverPercentageId))
          .reduce((max, r) => r.rollOverPercentage > max.rollOverPercentage ? r : max).rollOverPercentageId
      : undefined;

    this.originalDayId = this.selectedDayIds.length > 0
      ? this.rollOverDays
          .filter(r => this.selectedDayIds.includes(r.rollOverDayId))
          .reduce((max, r) => r.rollOverDay > max.rollOverDay ? r : max).rollOverDayId
      : undefined;
  }


  sendForApproval(): void {
    if (!this.selectedCompany) return;
    // Mark all fields as touched to show validation errors
    this.creditLineForm.markAllAsTouched();

    // Check if form is valid before submitting
    if (this.creditLineForm.invalid) {
      return;
    }
    const companyId = this.selectedCompany.companyId;
    const form = this.creditLineForm.value;

    // Get the ID of the option with the highest value among selected percentages
    const highestPercentageId = this.selectedPercentageIds.length > 0
      ? this.rollOverPercentages
          .filter(r => this.selectedPercentageIds.includes(r.rollOverPercentageId))
          .reduce((max, r) => r.rollOverPercentage > max.rollOverPercentage ? r : max).rollOverPercentageId
      : null;

    // Get the ID of the option with the highest value among selected days
    const highestDayId = this.selectedDayIds.length > 0
      ? this.rollOverDays
          .filter(r => this.selectedDayIds.includes(r.rollOverDayId))
          .reduce((max, r) => r.rollOverDay > max.rollOverDay ? r : max).rollOverDayId
      : null;

    // Check if any data has changed
    const hasChanges = this.checkForChanges(
      form.creditLimit,
      form.wageWithdrawalPercentage,
      highestPercentageId,
      highestDayId
    );

    if (!hasChanges) {
      this.toastr.info(
        'No changes detected. Please modify at least one field before sending for approval.',
        'No Changes'
      );
      return;
    }

    const payload = {
      companyId,
      creditLimit: Number(form.creditLimit),
      wageWithdrawalPercentage: Number(form.wageWithdrawalPercentage),
      rollOverDayId: highestDayId,
      rollOverPercentageId: highestPercentageId,
      requestedBy: this.loggedPortalAdminId,
    } as any;

    this.dataSubscriber$.add(
      this.creditService.createCreditLineRolloverRequest(companyId, payload).subscribe({
        next: (response) => {
          this.closeOffcanvas();
          this.toastr.success(
            response?.message || 'Credit line rollover request sent for approval successfully.',
            'Success'
          );

          // Refresh credit line requests and pending approvals after sending for approval
          if (this.activeTab === 'creditRiskRequests') {
            this.fetchCreditLineRequests(this.clrCurrentPage, this.clrPageSize, this.clrFilterValue || undefined);
          }
          if (this.activeTab === 'pendingApprovals') {
            this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize, this.paFilterValue || undefined);
          }
          if (this.activeTab === 'employerCredits') {
            this.fetchEmployerCredits(this.ecCurrentPage, this.ecPageSize, this.ecFilterValue || undefined);
          }
          // Forcefully refresh Pending Approvals tab data
          this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize, this.paFilterValue || undefined);
        },
        error: (error) => {
          this.toastr.error(
            error?.error?.message || error?.message || 'Error sending credit line rollover request.',
            'Error'
          );
        }
      })
    );
  }

  openPendingApproval(item: any): void {
    //console.log(item);
    this.selectedPending = item;
    this.offcanvasContext = 'pending';
    const offcanvasEl: any = this.offcanvasElementRef?.nativeElement;
    if (offcanvasEl && (window as any).bootstrap) {
      const bs = (window as any).bootstrap.Offcanvas.getInstance(offcanvasEl) || new (window as any).bootstrap.Offcanvas(offcanvasEl);
      bs.show();
    }
  }

  approvePending(): void {
    if (!this.selectedPending) return;

    const companyId = this.selectedPending.companyId;
    const companyCRId = this.selectedPending.companyCRId ?? this.selectedPending.companyCRDtlId;
    const approvedBy = this.loggedPortalAdminId;

    this.dataSubscriber$.add(
      this.creditService.approveCreditLineRolloverRequest({
        companyId, companyCRId, approvedBy, isApproved: true
      }).subscribe({
        next: (response) => {
          this.closeOffcanvas();
          this.toastr.success(
            response?.message || 'Credit line rollover request approved successfully.',
            'Success'
          );

          this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize, this.paFilterValue || undefined);
          // Refresh employer credits since approval updates credit information
          if (this.activeTab === 'employerCredits') {
            this.fetchEmployerCredits(this.ecCurrentPage, this.ecPageSize, this.ecFilterValue || undefined);
          }
        },
        error: (error) => {
          this.toastr.error(
            error?.error?.message || error?.message || 'Error approving credit line rollover request.',
            'Error'
          );
        }
      })
    );
  }

  rejectPending(): void {
    if (!this.selectedPending) return;

    const companyId = this.selectedPending.companyId;
    const companyCRId = this.selectedPending.companyCRId ?? this.selectedPending.companyCRDtlId;
    const rejectedBy = this.loggedPortalAdminId;

    this.dataSubscriber$.add(
      this.creditService.approveCreditLineRolloverRequest({
        companyId, companyCRId, approvedBy: rejectedBy, isApproved: false
      }).subscribe({
        next: (response) => {
          this.closeOffcanvas();
          this.toastr.success(
            'Credit line rollover request rejected successfully.',
            'Success'
          );

          this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize, this.paFilterValue || undefined);
          // Refresh employer credits after rejection
          if (this.activeTab === 'employerCredits') {
            this.fetchEmployerCredits(this.ecCurrentPage, this.ecPageSize, this.ecFilterValue || undefined);
          }
        },
        error: (error) => {
          this.toastr.error(
            error?.error?.message || error?.message || 'Error rejecting credit line rollover request.',
            'Error'
          );
        }
      })
    );
  }

  // =============================================================================================== //
  // Server Side Filtration
  // =============================================================================================== //

  onEcFilter(result: FilterResult): void {

    const filterValue = result['CompanyId']?.toString().trim();
    this.ecFilterValue = filterValue || '';

    if (!filterValue) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.ecCurrentPage = 1;
    this.searchText = '';

    this.fetchEmployerCredits(this.ecCurrentPage, this.ecPageSize, filterValue);
  }

  onEcClear(): void {
    this.ecCurrentPage = 1;
    this.searchText = '';
    this.ecFilterValue = '';
    this.ecError = '';
    this.fetchEmployerCredits(this.ecCurrentPage, this.ecPageSize);
  }

  // ============================================================== //

  onClrFilter(result: FilterResult): void {

    const filterValue = result['CompanyId']?.toString().trim();
    this.clrFilterValue = filterValue || '';

    if (!filterValue) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.clrCurrentPage = 1;
    this.clrSearchText = '';

    this.fetchCreditLineRequests(this.clrCurrentPage, this.clrPageSize, filterValue);
  }

  onClrClear(): void {
    this.clrCurrentPage = 1;
    this.clrSearchText = '';
    this.clrFilterValue = '';
    this.clrError = '';
    this.fetchCreditLineRequests(this.clrCurrentPage, this.clrPageSize);
  }

  // ============================================================== //

  onPaFilter(result: FilterResult): void {

    const filterValue = result['CompanyId']?.toString().trim();
    this.paFilterValue = filterValue || '';

    if (!filterValue) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.paCurrentPage = 1;
    this.pendingSearchText = '';

    this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize, filterValue);
  }

  onPaClear(): void {
    this.paCurrentPage = 1;
    this.pendingSearchText = '';
    this.paFilterValue = '';
    this.paError = '';
    this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize);
  }

  //================================================================================================//
  // Pagination logic //
  //================================================================================================//

  private updateEmployerCreditPage(): void {
    if (this.employerCreditPage < 1) this.employerCreditPage = 1;
    const start = (this.employerCreditPage - 1) * this.employerCreditPageSize;
    this.pagedEmployerCredits = this.filteredEmployerCredits.slice(start, start + this.employerCreditPageSize);
  }

  private resetEmployerCreditPagination(): void {
    this.employerCreditPage = 1;
    this.updateEmployerCreditPage();
  }

  // onEmployerCreditPageChange(paginatedData: any[]): void {
  //   this.pagedEmployerCredits = paginatedData;
  // }

  onEmployerCreditPageChange(event: PageEvent): void {
    this.ecCurrentPage = event.page;
    this.ecPageSize = event.pageSize;
    this.fetchEmployerCredits(this.ecCurrentPage, this.ecPageSize, this.ecFilterValue || undefined);
  }

  private updateEditorCreditRequestsPage(): void {
    if (this.creditLineRequestsPage < 1) this.creditLineRequestsPage = 1;
    const start = (this.creditLineRequestsPage - 1) * this.creditLineRequestsPageSize;
    this.pagedCreditLineRequests = this.filteredCreditLineRequests.slice(start, start + this.creditLineRequestsPageSize);
  }

  private resetCreditLineRequestsPagination(): void {
    this.creditLineRequestsPage = 1;
    this.updateEditorCreditRequestsPage();
  }

  // onCreditLineRequestsPageChange(paginatedData: any[]): void {
  //   this.pagedCreditLineRequests = paginatedData;
  // }
  onCreditLineRequestsPageChange(event: PageEvent): void {
    this.clrCurrentPage = event.page;
    this.clrPageSize = event.pageSize;
    this.fetchCreditLineRequests(this.clrCurrentPage, this.clrPageSize, this.clrFilterValue || undefined);
  }

  private updatePendingApprovalsPage(): void {
    if (this.pendingApprovalsPage < 1) this.pendingApprovalsPage = 1;
    const start = (this.pendingApprovalsPage - 1) * this.pendingApprovalsPageSize;
    this.pagedPendingApprovals = this.filteredPendingApprovals.slice(start, start + this.pendingApprovalsPageSize);
  }

  private resetPendingApprovalsPagination(): void {
    this.pendingApprovalsPage = 1;
    this.updatePendingApprovalsPage();
  }

  // onPendingApprovalsPageChange(paginatedData: any[]): void {
  //   this.pagedPendingApprovals = paginatedData;
  // }
  onPendingApprovalsPageChange(event: PageEvent): void {
    this.paCurrentPage = event.page;
    this.paPageSize = event.pageSize;
    this.fetchPendingApprovals(this.paCurrentPage, this.paPageSize, this.paFilterValue || undefined);
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }

}
